import React, { useMemo, useState } from 'react';
import { db } from '../data/store';
import { lists } from '../data/lists';

function localToday() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

function formatDate(value) {
  if (!value) return '—';
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString();
}

export default function SampleLoginQueue({ toast, session, onNavigate }) {
  const [company, setCompany] = useState('');
  const [companies, setCompanies] = useState(() => lists.getSources());
  const [addingCompany, setAddingCompany] = useState(false);
  const [newCompany, setNewCompany] = useState('');
  const [receivedDate, setReceivedDate] = useState(localToday());
  const [sampleCount, setSampleCount] = useState(1);
  const [queue, setQueue] = useState(() => db.getLoginQueue());
  const [saving, setSaving] = useState(false);

  const totalSamples = useMemo(
    () => queue.reduce((sum, item) => sum + (Number(item.sampleCount) || 0), 0),
    [queue]
  );

  const companyOptions = useMemo(() => {
    const names = [...companies];
    db.getSamples().forEach(sample => {
      const name = String(sample.source || '').trim();
      if (name && !names.some(existing => existing.toLowerCase() === name.toLowerCase())) {
        names.push(name);
      }
    });
    return names.sort((a, b) => a.localeCompare(b));
  }, [companies]);

  const saveNewCompany = () => {
    const cleanName = newCompany.trim();
    if (!cleanName) {
      toast('warn', 'Enter the new company name');
      return;
    }

    const existing = companyOptions.find(
      name => name.toLowerCase() === cleanName.toLowerCase()
    );
    if (existing) {
      setCompany(existing);
      setAddingCompany(false);
      setNewCompany('');
      toast('info', `${existing} is already in the company list`);
      return;
    }

    lists.addTo('sources', cleanName);
    setCompanies(lists.getSources());
    setCompany(cleanName);
    setAddingCompany(false);
    setNewCompany('');
    toast('success', `${cleanName} added to the company list`);
  };

  const addEntry = async event => {
    event.preventDefault();
    const cleanCompany = company.trim();
    if (!cleanCompany) {
      toast('warn', 'Enter the company name');
      return;
    }
    if (!receivedDate) {
      toast('warn', 'Select the received date');
      return;
    }
    const count = Number(sampleCount);
    if (!Number.isInteger(count) || count < 1) {
      toast('warn', 'Number of samples must be at least 1');
      return;
    }

    setSaving(true);
    try {
      await db.addLoginQueueItem({
        company: cleanCompany,
        receivedDate,
        sampleCount: count,
        addedBy: session?.displayName || session?.username || '',
      });
      setQueue(db.getLoginQueue());
      setCompany('');
      setSampleCount(1);
      toast('success', `${count} sample${count === 1 ? '' : 's'} added to Received`);
    } catch (error) {
      toast('error', error.message || 'Could not add incoming samples');
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async item => {
    try {
      await db.removeLoginQueueItem(item.id);
      setQueue(db.getLoginQueue());
      toast('success', `${item.company} removed from the login queue`);
    } catch (error) {
      toast('error', error.message || 'Could not remove the entry');
    }
  };

  return (
    <div className="content" style={{height:'100%',overflowY:'auto',boxSizing:'border-box'}}>
      <div className="page-header" style={{marginBottom:14}}>
        <div>
          <h1 style={{marginBottom:3}}>Incoming Samples</h1>
          <p style={{margin:0,color:'#667085',fontSize:12}}>
            Record packages that have arrived and still need to be logged into Sample Setup.
          </p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{background:'#eaf4ff',color:'#1a7dc4',padding:'5px 10px',
            borderRadius:14,fontSize:11,fontWeight:700}}>
            {queue.length} package{queue.length === 1 ? '' : 's'} · {totalSamples} sample{totalSamples === 1 ? '' : 's'}
          </span>
          <button className="btn btn-secondary" onClick={() => onNavigate('labboard')}>
            View Received Column
          </button>
        </div>
      </div>

      <div className="card" style={{padding:16,marginBottom:14}}>
        <form onSubmit={addEntry} style={{display:'grid',gridTemplateColumns:'minmax(260px,2fr) minmax(170px,1fr) minmax(150px,.8fr) auto',gap:12,alignItems:'end'}}>
          <div className="form-group" style={{margin:0}}>
            <label>Company Name *</label>
            <div style={{display:'flex',gap:7}}>
              <select value={company} onChange={e => setCompany(e.target.value)}
                autoFocus style={{flex:1,minWidth:0}}>
                <option value="">Select company</option>
                {companyOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <button className="btn btn-secondary" type="button"
                onClick={() => setAddingCompany(value => !value)}
                style={{height:34,whiteSpace:'nowrap'}}>
                {addingCompany ? 'Cancel' : '＋ Add Company'}
              </button>
            </div>
            {addingCompany && (
              <div style={{display:'flex',gap:7,marginTop:7}}>
                <input value={newCompany} onChange={e => setNewCompany(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveNewCompany();
                    }
                  }}
                  placeholder="Enter new company name" autoFocus style={{flex:1,minWidth:0}} />
                <button className="btn btn-success" type="button" onClick={saveNewCompany}
                  style={{height:34,whiteSpace:'nowrap'}}>
                  Save Company
                </button>
              </div>
            )}
          </div>
          <div className="form-group" style={{margin:0}}>
            <label>Date Received *</label>
            <input type="date" value={receivedDate} onChange={e => setReceivedDate(e.target.value)} />
          </div>
          <div className="form-group" style={{margin:0}}>
            <label>Number of Samples *</label>
            <input type="number" min="1" step="1" value={sampleCount}
              onChange={e => setSampleCount(e.target.value)} />
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving}
            style={{height:34,whiteSpace:'nowrap'}}>
            {saving ? 'Adding…' : '＋ Add to Received'}
          </button>
        </form>
      </div>

      <div className="card" style={{overflow:'hidden'}}>
        <div style={{padding:'10px 14px',background:'#1a7dc4',color:'white',fontWeight:700,fontSize:13}}>
          Awaiting Sample Login
        </div>
        {queue.length === 0 ? (
          <div style={{padding:36,textAlign:'center',color:'#98a2b3'}}>
            No incoming samples are waiting to be logged in.
          </div>
        ) : (
          <table className="data-table" style={{margin:0}}>
            <thead>
              <tr>
                <th>Company</th>
                <th>Date Received</th>
                <th style={{textAlign:'center'}}>Samples</th>
                <th>Added By</th>
                <th style={{width:180}}></th>
              </tr>
            </thead>
            <tbody>
              {queue.map(item => (
                <tr key={item.id}>
                  <td style={{fontWeight:700,color:'#1e3060'}}>{item.company}</td>
                  <td>{formatDate(item.receivedDate)}</td>
                  <td style={{textAlign:'center',fontWeight:700}}>{item.sampleCount}</td>
                  <td>{item.addedBy || '—'}</td>
                  <td style={{textAlign:'right'}}>
                    <button className="btn btn-success" onClick={() => removeEntry(item)}
                      title="Remove after all samples in this package have been entered">
                      ✓ Samples Logged In
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
